/**
 * Hover card for one week of the balance-evolution candlestick: the week's OHLC
 * plus a scrollable per-account high/low breakdown.
 *
 * Shared by the Finance dashboard widget and the Account balance evolution
 * report so the two never drift — they render the same endpoint's rows and a
 * reader moving between them should see the same card.
 */

import type { BalanceEvolutionWeekDto } from '../../api/finance';
import { formatEur } from '../../screens/finance/finance-format';
import styles from '../../screens/finance/finance.module.css';
import { shortWeek, useChartTheme } from './charts';
import tip from './BalanceTooltip.module.css';

export function BalanceTooltip({ week }: { week: BalanceEvolutionWeekDto | null }) {
  const theme = useChartTheme();
  if (!week) return null;
  const rows: Array<[string, number]> = [
    ['Open', week.open],
    ['Close', week.close],
    ['High', week.high],
    ['Low', week.low],
  ];
  return (
    <div className={tip.candleTip} style={{ borderColor: theme.line }}>
      <div className={tip.candleTipHead}>Week of {shortWeek(week.weekStart)}</div>
      <div className={tip.candleTipOhlc}>
        {rows.map(([k, v]) => (
          <div key={k} className={tip.candleTipRow}>
            <span className={tip.candleTipKey}>{k}</span>
            <span className={`${styles.num} ${tip.candleTipVal}`}>{formatEur(v)}</span>
          </div>
        ))}
      </div>
      {week.accounts.length > 0 && (
        <>
          <div className={tip.candleTipSub}>Account breakdown (high / low)</div>
          <div className={tip.candleTipAccounts}>
            {week.accounts.map((a) => (
              <div key={a.accountId} className={tip.candleTipAcct}>
                <span className={tip.candleTipAcctName}>{a.name}</span>
                <span className={`${styles.num} ${tip.candleTipHigh}`}>{formatEur(a.high)}</span>
                <span className={`${styles.num} ${tip.candleTipLow}`}>{formatEur(a.low)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
